import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { appointmentService } from '../utils/api'
import './Payment.css'

function Payment() {
  const navigate = useNavigate()

  const [appointment, setAppointment] = useState(null)
  const [paymentProof, setPaymentProof] = useState(null)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    // Get appointment from session storage
    const pending = sessionStorage.getItem('pendingAppointment')
    if (!pending) {
      navigate('/appointment')
      return
    }
    setAppointment(JSON.parse(pending))
  }, [navigate])

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg']
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a PDF, PNG, or JPEG file')
        return
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB')
        return
      }
      setPaymentProof(file)
      setError(null)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!paymentProof) {
      setError('Please upload payment proof before completing')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('name', appointment.name)
      formData.append('email', appointment.email)
      formData.append('phone', appointment.phone)
      formData.append('preferredDate', appointment.preferredDate)
      formData.append('preferredTime', appointment.preferredTime)
      formData.append('serviceType', appointment.serviceType)
      formData.append('description', appointment.description)
      formData.append('paymentProof', paymentProof)
      formData.append('recaptchaToken', appointment.recaptchaToken)

      // Create appointment with payment proof
      await appointmentService.create(formData)
      
      setSuccess(true)
      // Clear session data
      sessionStorage.removeItem('pendingAppointment')
      // Redirect to home after 3 seconds
      setTimeout(() => {
        navigate('/')
      }, 3000)
    } catch (err) {
      setError(err.message || 'Failed to complete payment. Please try again.')
      console.error('Error:', err)
    } finally {
      setLoading(false)
    }
  }

  if (!appointment) return null

  if (success) {
    const amount = (parseInt(import.meta.env.VITE_APPOINTMENT_PRICE_CENTS) / 100).toFixed(2)
    return (
      <div className="payment">
        <div className="container">
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h1>Payment Received!</h1>
            <p>We have received your payment proof and your appointment has been confirmed.</p>
            <p>A confirmation email will be sent to <strong>{appointment.email}</strong></p>
            <p className="small">Redirecting to home page...</p>
          </div>
        </div>
      </div>
    )
  }

  const amount = (parseInt(import.meta.env.VITE_APPOINTMENT_PRICE_CENTS) / 100).toFixed(2)

  return (
    <div className="payment">
      <div className="container">
        <section className="payment-intro">
          <h1>Complete Your Payment</h1>
          <p>Secure payment to confirm your appointment</p>
        </section>

        <div className="payment-wrapper">
          {/* Appointment Summary */}
          <section className="payment-summary">
            <h2>Appointment Summary</h2>
            
            <div className="summary-item">
              <span>Name:</span>
              <strong>{appointment.name}</strong>
            </div>

            <div className="summary-item">
              <span>Email:</span>
              <strong>{appointment.email}</strong>
            </div>

            <div className="summary-item">
              <span>Phone:</span>
              <strong>{appointment.phone}</strong>
            </div>

            <div className="summary-item">
              <span>Service Type:</span>
              <strong>{appointment.serviceType}</strong>
            </div>

            <div className="summary-item">
              <span>Preferred Date:</span>
              <strong>{new Date(appointment.preferredDate).toLocaleDateString()}</strong>
            </div>

            <div className="summary-item">
              <span>Preferred Time:</span>
              <strong>{appointment.preferredTime}</strong>
            </div>

            <div className="summary-divider"></div>

            <div className="summary-item total">
              <span>Consultation Fee:</span>
              <strong>${amount}</strong>
            </div>

            <p className="payment-note">
              📱 Please send your payment via e-transfer to complete your appointment booking.
            </p>
          </section>

          {/* E-Transfer Payment */}
          <section className="payment-form-section">
            <h2>E-Transfer Payment</h2>

            <div className="transfer-info">
              <p>Please send <strong>${amount} CAD</strong> via e-transfer to:</p>
              <div className="transfer-details">
                <p><strong>Phone (Work):</strong> <a href="tel:403-369-3711">403-369-3711</a></p>
                <p><strong>Phone (Cell):</strong> <a href="tel:403-700-1368">403-700-1368</a></p>
              </div>
              <p className="transfer-note">Please include your name and appointment details in the transfer message.</p>
              
              <form onSubmit={handleSubmit} className="payment-form">
                <div className="form-group">
                  <label htmlFor="paymentProof">Upload Payment Receipt *</label>
                  <p className="field-hint">Upload a screenshot or file proving you sent the e-transfer (PDF, PNG, or JPEG)</p>
                  <input
                    type="file"
                    id="paymentProof"
                    name="paymentProof"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={handleFileChange}
                    disabled={loading}
                    required
                    className="file-input"
                  />
                  {paymentProof && (
                    <p className="file-selected">✓ {paymentProof.name} selected</p>
                  )}
                </div>

                {error && <div className="alert alert-error">{error}</div>}

                <p className="confirmation-text">
                  Once we verify your payment receipt, your appointment will be confirmed and you will receive a confirmation email.
                </p>

                <button
                  type="submit"
                  className="btn btn-primary btn-large"
                  disabled={loading || !paymentProof}
                >
                  {loading ? 'Processing...' : 'Payment Completed'}
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate('/appointment')}
                  disabled={loading}
                >
                  Back to Appointment
                </button>
              </form>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

export default Payment
